<script setup>
import { useMetrics } from "@/composables/use.metrics";
import { configCreator, loaderUrl } from "@/lib/unityConfig";
import { isMobile } from "@basitcodeenv/vue3-device-detect";
import { defineProps, onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
});

const instanceRef = ref();
const canWatchEndGame = ref(false);


const { eventTypes, hitType, hit } = useMetrics();

function closeVideo() {
  if (!showVideo.value) return;
  showVideo.value = false;
}

function updateCanvasSize() {
  const canvas = document.querySelector("#unity-canvas");
  if (!canvas) return;
  canvas.style.width = window.innerWidth + "px";
  canvas.style.height = window.innerHeight + "px";

  return canvas;
}

function init() {
  setTimeout(() => window.addEventListener('click', closeVideo));
  window.addEventListener('orientationchange', updateCanvasSize);
  window.addEventListener('resize', updateCanvasSize);
  const canvas = updateCanvasSize();

  if (!canvas) {
    return;
  }

  const config = configCreator(unityShowBanner, isMobile);

  const script = document.createElement("script");
  script.src = loaderUrl;
  script.id = "test";
  script.onload = () => {
    const loading = document.querySelector("#loading");
    try {
      window
        .createUnityInstance(canvas, config, (progress) => {
          try {
            loading.innerHTML = (100 * progress).toFixed(0) + "%";
          } catch (error) {
            console.error("Error updating loading progress:", error);
          }
        })
        .then((unityInstance) => {
          instanceRef.value = unityInstance;
          showLoading.value = false;

          if (!isMobile) {
            instanceRef.value?.SetFullscreen(1);
          }
        })
        .catch((message) => {
          console.log(message);
        });
    } catch (error) {
      console.error("Error creating Unity instance:", error);
    }
  };

  document.body?.appendChild(script);
}

function unityShowBanner(msg, type) {
  console.log(`unityShowBanner type: [${type}]: ${msg}`);
}

onMounted(() => init());

const showVideo = ref(true);
const video = ref();
const showLoading = ref(true);

const videoWatcher = watch(video, (value) => {
  if (!value) return;
  videoWatcher.stop();
  value.muted = false;
});

function skipInitVideo() {
  hit(eventTypes.FIREWORK, hitType.SKIP_VIDEO);
  showVideo.value = false;
}

const showVideoWatcher = watch(showVideo, (value) => {
  if (value) return;
  showVideoWatcher.stop();
});

function hitEndGame() {
  if (!canWatchEndGame.value) return;
  hit(eventTypes.FIREWORK, hitType.END_GAME);
}

function runGameWatcher() {
  canWatchEndGame.value = true;
}

watch(() => props.modelValue, (state) => {
  document.body.style.overflowY = state ? 'hidden' : 'visible';
}, { immediate: true });


onMounted(() => {
  document.addEventListener('hit-run-game', runGameWatcher);
  window.addEventListener('beforeunload', hitEndGame);
})

onBeforeUnmount(() => {
  const script = document.getElementById("test");
  window.removeEventListener('click', closeVideo);
  window.removeEventListener('orientationchange', updateCanvasSize);
  window.removeEventListener('resize', updateCanvasSize);
  window.removeEventListener('beforeunload', hitEndGame);
  document.removeEventListener('hit-run-game', runGameWatcher);
  instanceRef.value?.Quit();
  if (script) {
    script.remove();
  }
});
</script>

<template>
  <div class="canvas-modal">
    <div class="canvas-modal__content">
      <Transition name="fade">
        <div v-if="showVideo" class="canvas-video">
          <video
            v-if="showVideo"
            class="canvas-modal__video"
            ref="video"
            preload="auto"
            autoplay
            playsinline
            webkit-playsinline
            src="https://meta-vdnh-s3.mos.ru/static_content/video/moscow-preview.mp4"
            @ended="skipInitVideo"
          />
        </div>
      </Transition>
      <div
        v-show="!showVideo"
        id="unity-container"
        class="unity-desktop"
        :class="{ 'unity-mobile': isMobile }"
      >
        <canvas
          id="unity-canvas"
          width="960"
          height="600"
          tabindex="-1"
          :class="{ 'unity-mobile': isMobile }"
        />
        <div v-show="showLoading" id="unity-loading-bar" class="loading-bar">
          <div id="unity-logo">
            <img src="/logo.gif" alt="logo" />
          </div>
          <div id="loading" />
        </div>
      </div>
    </div>
    <button v-if="showVideo" class="link" @click="skipInitVideo">Пропустить</button>
  </div>
</template>

<style lang="scss" scoped>
.canvas-modal {
  width: 100vw;
  height: 100vh;
  position: fixed;
  inset: 0;
  z-index: 200;
  background-color: #fff;

  &__content {
    width: 100%;
    height: 100%;
    position: relative;
  }

  .canvas-video {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.9);
    z-index: 1000;
  }

  &__video {
    width: 100%;
    height: 100%;
  }

  .link {
    width: max-content;
    position: fixed;
    bottom: 100px;
    right: 100px;
    padding: 13px;
    font-size: 16px;
    line-height: 140%;
    justify-content: center;
    z-index: 100000000000000;
  }

  .loading-bar {
    position: fixed;
    inset: 0;
    width: 100vw !important;
    height: 100vh !important;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  #unity-logo img {
    width: 250px;
  }

  #loading {
    margin-top: 10px;
    font-family: sans-serif;
    font-size: 20px;
    font-weight: 500;
    text-align: center;
    color: #013b99;
  }

  .unity-mobile {
    position: fixed;
    width: 100%;
    height: 100%;
  }
}
</style>
